/**
 * URL Safety Checker - Analyzes URLs for security threats and suspicious patterns
 */

import { analyzeInvisibleCharacters, formatCategorySummary, type InvisibleCharAnalysisResult } from './invisibleCharacterAnalyzer';

export interface URLComponents {
  protocol: string;
  hostname: string;
  port?: string;
  pathname: string;
  search: string;
  hash: string;
  username?: string;
  password?: string;
  punycode?: string;
}

export interface SecurityIssue {
  severity: 'high' | 'medium' | 'low';
  type: string;
  description: string;
}

export interface URLAnalysis {
  components: URLComponents;
  issues: SecurityIssue[];
  riskLevel: 'safe' | 'suspicious' | 'dangerous';
  score: number; // 0-100, higher is safer
  invisibleCharAnalysis?: InvisibleCharAnalysisResult;
}

/**
 * Parse URL into components
 */
export const parseURL = (urlString: string): URLComponents | null => {
  try {
    const url = new URL(urlString);
    
    // Try to convert IDN to punycode
    let punycode: string | undefined;
    // eslint-disable-next-line no-control-regex
    if (/[^\x00-\x7F]/.test(url.hostname)) {
      try {
        punycode = url.hostname; // Browser automatically converts to punycode in URL object
      } catch {
        // Ignore punycode conversion errors
      }
    }
    
    return {
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port || undefined,
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
      username: url.username || undefined,
      password: url.password || undefined,
      punycode,
    };
  } catch {
    return null;
  }
};

/**
 * Detect homoglyph attacks in domain
 */
export const detectHomoglyphs = (domain: string): SecurityIssue[] => {
  const issues: SecurityIssue[] = [];
  
  // Check for non-ASCII characters
  // eslint-disable-next-line no-control-regex
  if (/[^\x00-\x7F]/.test(domain)) {
    // Common homoglyph characters
    const homoglyphs: Record<string, string> = {
      'а': 'a (Cyrillic)',
      'е': 'e (Cyrillic)',
      'о': 'o (Cyrillic)',
      'р': 'p (Cyrillic)',
      'с': 'c (Cyrillic)',
      'у': 'y (Cyrillic)',
      'х': 'x (Cyrillic)',
      'ι': 'i (Greek)',
      'ο': 'o (Greek)',
      'ν': 'v (Greek)',
      '0': 'O (digit zero)',
      '1': 'l (digit one)',
    };
    
    for (const [char, description] of Object.entries(homoglyphs)) {
      if (domain.includes(char)) {
        issues.push({
          severity: 'high',
          type: 'Homoglyph Attack',
          description: `Domain contains '${char}' which looks like ${description}. This may be a phishing attempt.`,
        });
      }
    }
    
    // Generic warning if non-ASCII but no specific homoglyphs detected
    if (issues.length === 0) {
      issues.push({
        severity: 'medium',
        type: 'IDN Domain',
        description: 'Domain contains non-ASCII characters (Internationalized Domain Name). Verify this is expected.',
      });
    }
  }
  
  return issues;
};

/**
 * Detect invisible characters in URL
 */
export const detectInvisibleCharactersInURL = (components: URLComponents): SecurityIssue[] => {
  const issues: SecurityIssue[] = [];
  
  // Combine all URL parts (excluding protocol)
  // Decode URL components to detect invisible characters that may be percent-encoded
  // Use try-catch to handle malformed URL encoding
  let pathname = components.pathname;
  let search = components.search;
  let hash = components.hash;
  
  try {
    pathname = decodeURIComponent(components.pathname);
  } catch {
    // Keep original if decoding fails
  }
  
  try {
    search = decodeURIComponent(components.search);
  } catch {
    // Keep original if decoding fails
  }
  
  try {
    hash = decodeURIComponent(components.hash);
  } catch {
    // Keep original if decoding fails
  }
  
  const urlText = `${components.hostname}${pathname}${search}${hash}`;
  
  // Use unified analyzer (excluding regular spaces, no security analysis needed here)
  const analysis = analyzeInvisibleCharacters(urlText, {
    includeSpaces: false,
    includeSecurityAnalysis: false,
    context: 'url'
  });
  
  if (analysis.hasInvisibleChars) {
    // Determine severity based on character types
    let severity: SecurityIssue['severity'] = 'medium';
    if (analysis.categorySummary.has('zero-width') || analysis.categorySummary.has('directional')) {
      severity = 'high';
    }
    
    const categoryList = formatCategorySummary(analysis.categorySummary);
    
    issues.push({
      severity,
      type: 'Invisible Characters',
      description: `URL contains ${analysis.count} invisible character${analysis.count > 1 ? 's' : ''} (${categoryList}). This may be used to disguise the true destination or bypass filters.`,
    });
  }
  
  return issues;
};

/**
 * Check for typosquatting patterns
 */
export const checkTyposquatting = (domain: string): SecurityIssue[] => {
  const issues: SecurityIssue[] = [];
  
  // Common typosquatting patterns
  const patterns = [
    { regex: /goog[o0]le/i, target: 'google', description: 'Typosquatting of Google' },
    { regex: /micr[o0]s[o0]ft/i, target: 'microsoft', description: 'Typosquatting of Microsoft' },
    { regex: /faceb[o0][o0]k/i, target: 'facebook', description: 'Typosquatting of Facebook' },
    { regex: /am[a@]z[o0]n/i, target: 'amazon', description: 'Typosquatting of Amazon' },
    { regex: /appl[e3]/i, target: 'apple', description: 'Typosquatting of Apple' },
    { regex: /payp[a@]l/i, target: 'paypal', description: 'Typosquatting of PayPal' },
  ];
  
  for (const pattern of patterns) {
    if (pattern.regex.test(domain) && !domain.toLowerCase().includes(pattern.target)) {
      issues.push({
        severity: 'high',
        type: 'Typosquatting',
        description: `Possible ${pattern.description}. Domain mimics a well-known brand.`,
      });
    }
  }
  
  // Check for repeated characters (excluding common patterns like 'www')
  // Match 3+ repeated characters, but exclude 'www' and other legitimate patterns
  const hasRepeatedChars = /(.)\1{2,}/.test(domain);
  const isWWW = /^www\./.test(domain) || domain.includes('.www.') || domain === 'www';
  
  if (hasRepeatedChars && !isWWW) {
    issues.push({
      severity: 'low',
      type: 'Suspicious Pattern',
      description: 'Domain contains repeated characters which may indicate typosquatting.',
    });
  }
  
  return issues;
};

/**
 * Check for suspicious TLDs
 */
const checkSuspiciousTLD = (hostname: string): SecurityIssue[] => {
  const issues: SecurityIssue[] = [];
  
  const suspiciousTLDs = ['.tk', '.ml', '.ga', '.cf', '.gq', '.xyz', '.top', '.work', '.click', '.link'];
  const tld = hostname.substring(hostname.lastIndexOf('.'));
  
  if (suspiciousTLDs.includes(tld.toLowerCase())) {
    issues.push({
      severity: 'medium',
      type: 'Suspicious TLD',
      description: `Domain uses ${tld} which is commonly associated with malicious websites.`,
    });
  }
  
  return issues;
};

/**
 * Check for suspicious URL patterns
 */
const checkSuspiciousPatterns = (url: URLComponents): SecurityIssue[] => {
  const issues: SecurityIssue[] = [];
  
  // Check for credentials in URL
  if (url.username || url.password) {
    issues.push({
      severity: 'high',
      type: 'Embedded Credentials',
      description: 'URL contains embedded username/password. This is insecure and often used in phishing.',
    });
  }
  
  // Check for IP address as hostname
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(url.hostname)) {
    issues.push({
      severity: 'medium',
      type: 'IP Address',
      description: 'URL uses IP address instead of domain name. This may indicate suspicious activity.',
    });
  }
  
  // Check for suspicious schemes
  if (url.protocol === 'javascript:' || url.protocol === 'data:') {
    issues.push({
      severity: 'high',
      type: 'Dangerous Protocol',
      description: `URL uses ${url.protocol} which can execute arbitrary code or embed content.`,
    });
  }
  
  // Check for HTTP (not HTTPS)
  if (url.protocol === 'http:' && !url.hostname.includes('localhost') && !url.hostname.includes('127.0.0.1')) {
    issues.push({
      severity: 'low',
      type: 'Unencrypted Connection',
      description: 'URL uses HTTP instead of HTTPS. Data transmitted will not be encrypted.',
    });
  }
  
  // Check for suspicious ports
  const suspiciousPorts = ['8080', '8888', '3128', '1080'];
  if (url.port && suspiciousPorts.includes(url.port)) {
    issues.push({
      severity: 'low',
      type: 'Unusual Port',
      description: `URL uses port ${url.port} which is commonly used for proxies or testing.`,
    });
  }
  
  // Check for excessive subdomains
  const subdomains = url.hostname.split('.').length - 2;
  if (subdomains > 3) {
    issues.push({
      severity: 'low',
      type: 'Excessive Subdomains',
      description: `URL has ${subdomains} subdomains. This may be used to obscure the actual domain.`,
    });
  }
  
  // Check for URL shorteners in path
  const shortenerPatterns = ['bit.ly', 'tinyurl', 'goo.gl', 't.co', 'ow.ly'];
  if (shortenerPatterns.some(pattern => url.hostname.includes(pattern))) {
    issues.push({
      severity: 'medium',
      type: 'URL Shortener',
      description: 'URL shortener detected. The actual destination is hidden and could be malicious.',
    });
  }
  
  // Check for suspicious keywords in path
  const suspiciousKeywords = ['login', 'signin', 'verify', 'account', 'update', 'secure', 'banking'];
  const pathLower = url.pathname.toLowerCase();
  if (suspiciousKeywords.some(keyword => pathLower.includes(keyword)) && url.protocol === 'http:') {
    issues.push({
      severity: 'high',
      type: 'Suspicious Path',
      description: `URL contains security-related keywords but uses unencrypted HTTP.`,
    });
  }
  
  return issues;
};

/**
 * Calculate risk score (0-100, higher is safer)
 */
const calculateRiskScore = (issues: SecurityIssue[]): number => {
  let score = 100;
  
  for (const issue of issues) {
    switch (issue.severity) {
      case 'high':
        score -= 30;
        break;
      case 'medium':
        score -= 15;
        break;
      case 'low':
        score -= 5;
        break;
    }
  }
  
  return Math.max(0, score);
};

/**
 * Determine risk level from score
 */
const getRiskLevel = (score: number): 'safe' | 'suspicious' | 'dangerous' => {
  if (score >= 80) return 'safe';
  if (score >= 50) return 'suspicious';
  return 'dangerous';
};

/**
 * Analyze URL for security issues
 */
export const analyzeURL = (urlString: string): URLAnalysis | null => {
  const components = parseURL(urlString);
  
  if (!components) {
    return null;
  }
  
  // Detect invisible characters first
  // Decode URL components to detect invisible characters that may be percent-encoded
  // Use try-catch to handle malformed URL encoding
  let pathname = components.pathname;
  let search = components.search;
  let hash = components.hash;
  
  try {
    pathname = decodeURIComponent(components.pathname);
  } catch {
    // Keep original if decoding fails
  }
  
  try {
    search = decodeURIComponent(components.search);
  } catch {
    // Keep original if decoding fails
  }
  
  try {
    hash = decodeURIComponent(components.hash);
  } catch {
    // Keep original if decoding fails
  }
  
  const urlText = `${components.hostname}${pathname}${search}${hash}`;
  
  // Use unified analyzer for invisible character detection with security analysis
  const invisibleCharAnalysis = analyzeInvisibleCharacters(urlText, {
    includeSpaces: false,
    includeSecurityAnalysis: true,
    context: 'url'
  });
  
  const issues: SecurityIssue[] = [
    ...detectHomoglyphs(components.hostname),
    ...checkTyposquatting(components.hostname),
    ...checkSuspiciousTLD(components.hostname),
    ...checkSuspiciousPatterns(components),
    ...detectInvisibleCharactersInURL(components),
  ];
  
  const score = calculateRiskScore(issues);
  const riskLevel = getRiskLevel(score);
  
  return {
    components,
    issues,
    riskLevel,
    score,
    invisibleCharAnalysis: invisibleCharAnalysis.hasInvisibleChars ? invisibleCharAnalysis : undefined,
  };
};

/**
 * Get example URL for testing
 */
export const getExampleURL = (): string => {
  return 'https://www.example.com/path/to/page?param1=value1&param2=value2#section';
};

/**
 * Get suspicious example URL for testing
 */
export const getSuspiciousExampleURL = (): string => {
  // Add multiple invisible characters for testing:
  // - Zero-Width Space (U+200B) in "paypal"
  // - Zero-Width Non-Joiner (U+200C) in "login"
  // - Left-to-Right Mark (U+200E) in path
  return 'https://pay\u200Bpal.com/log\u200Cin\u200E/account?user=admin&redirect=http://evil.com';
};

